import React from 'react';
import { Columns } from '@components/Columns';
import {
  ClipPathBox,
  StyledUniversalRegistry,
  StyledTextPairing,
  StyledGoldThread1,
  StyledImagesRow,
  StyledCenterImage,
  LottiesContainer,
  StateIconTextContainer,
  StateIconLottie,
  HeroTextContainer,
  StyledGoldThreadBack,
  StyledImagesRow2,
  StyledMainFlex,
  StyledCenterImageShape,
  IconTextPairing,
  BrowserImageWrapper,
  StyledSVGWrapper,
  StyledMobileImage,
  UniversalRegistryGraphicWrapper,
  StyledHeroTextColumn,
} from './RegistrySection.styles';
import { registryPageData } from '@data-ts/pages/registry';
import { useRegistryController } from './Registry.controller';
import { useBrowserImageController } from './BrowserImage.controller';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';

const universalRegistrySectionData = registryPageData.universalRegistrySectionV2;

interface UniversalRegistryProps {}

const imagesData = {
  firstColumnImages: [
    {
      id: '1', // kitchen
      shapeURL: '1',
      imageURL: 'registry-v2/universal-registry/kitchen-v2.png',
      width: '200px',
      height: '200px',
      bkgSize: '100%',
      bkgPosition: '0px 0px',
      left: 270,
      bottom: undefined,
      top: 20,
      right: undefined,
    },
  ],
  secondColumnImages: [
    {
      id: '2', // car
      shapeURL: 'clipUniversal2',
      imageURL: 'registry-v2/universal-registry/car-v2.png',
      width: '200px',
      height: '200px',
      bkgSize: '100%',
      bkgPosition: '0px 0px',
      left: undefined,
      bottom: undefined,
      top: -10,
      right: 160,
    },
  ],
};

const smallTitleProps = { titleVariant: 'featureHed17', bodyVariant: 'featureCopy17', titleColor: 'neutra6', alignment: 'center' } as const;

export const RegistrySection: React.FC<UniversalRegistryProps> = () => {
  const {
    sectionRef,
    universalRegistryTextPairRef,
    iconsContainerRef,
    giftsLottieRef,
    cashLottieRef,
    experiencesLottieRef,
    charityLottieRef,
    isScrolledIntoView,
    setScrolledIntoView,
  } = useRegistryController();
  const { browserImageRef } = useBrowserImageController();
  return (
    <StyledUniversalRegistry
      marginTop={[9, 0]}
      marginBottom={6}
      paddingBottom={[9, 11]}
      ref={sectionRef}
      as="section"
      id="Universal_Registry_V2"
    >
      <HeroTextContainer ref={universalRegistryTextPairRef} paddingTop={8} paddingBottom={3}>
        <StyledHeroTextColumn centered>
          <Columns.Column width={6}>
            <StyledTextPairing
              eyebrow={universalRegistrySectionData.eyeBrow}
              eyebrowColor="neutra4"
              eyebrowVariant="sectionEyeBrow20"
              eyebrowTagName="h2"
              title={universalRegistrySectionData.title}
              titleVariant="hed56"
              titleColor="neutra6"
              titleTagName="h2"
              body={universalRegistrySectionData.content}
              bodyVariant="dek20"
              bodyColor="neutra5"
              alignment={'center'}
            />
          </Columns.Column>
        </StyledHeroTextColumn>
        <LottiesContainer marginTop={[6, 8]} ref={iconsContainerRef}>
          <StateIconTextContainer>
            <StateIconLottie ref={giftsLottieRef} />
            <IconTextPairing title={universalRegistrySectionData.registryTypeGifts} {...smallTitleProps} />
          </StateIconTextContainer>
          <StateIconTextContainer>
            <StateIconLottie ref={cashLottieRef} />
            <IconTextPairing title={universalRegistrySectionData.registryTypeCash} {...smallTitleProps} />
          </StateIconTextContainer>
          <StateIconTextContainer>
            <StateIconLottie ref={experiencesLottieRef} />
            <IconTextPairing title={universalRegistrySectionData.registryTypeExperiences} {...smallTitleProps} />
          </StateIconTextContainer>
          <StateIconTextContainer>
            <StateIconLottie ref={charityLottieRef} />
            <IconTextPairing title={universalRegistrySectionData.registryTypeCharity} {...smallTitleProps} />
          </StateIconTextContainer>
        </LottiesContainer>
      </HeroTextContainer>
      <UniversalRegistryGraphicWrapper paddingTop={[7, 10]}>
        <StyledGoldThreadBack justifyContent="center">
          <LazyLoadWrapper beforeLoad={() => setScrolledIntoView(true)}>
            <div />
          </LazyLoadWrapper>
          <StyledGoldThread1 isMainSrcShown={isScrolledIntoView} src="registry-v2/universal-registry/bg-v2.png" />
          <StyledImagesRow>
            {imagesData.firstColumnImages.map(item => (
              <ClipPathBox
                key={`v2_1_${item.id}`}
                id={`universal_V2_1_${item.id}`}
                width={item.width}
                height={item.height}
                clipId={item.shapeURL}
                imgUrl={cloudAssetUrl(item.imageURL)}
                bkgSize={item.bkgSize}
                bkgPosition={item.bkgPosition}
                top={item.top}
                bottom={item.bottom}
                left={item.left}
                right={item.right}
              />
            ))}
          </StyledImagesRow>
          <StyledImagesRow2>
            {imagesData.secondColumnImages.map(item => (
              <ClipPathBox
                key={`v2_2_${item.id}`}
                id={`universal_V2_2_${item.id}`}
                width={item.width}
                height={item.height}
                clipId={item.shapeURL}
                imgUrl={cloudAssetUrl(item.imageURL)}
                bkgSize={item.bkgSize}
                bkgPosition={item.bkgPosition}
                top={item.top}
                bottom={item.bottom}
                left={item.left}
                right={item.right}
              />
            ))}
          </StyledImagesRow2>
          <StyledMainFlex justifyContent="center" alignItems="center">
            <StyledCenterImageShape
              marginTop={[6, 8]}
              marginRight={[8]}
              justifyContent="center"
              alignItems="center"
              imageSrc="registry-v2/universal-registry/websitie-browser-frame.svg"
            >
              <BrowserImageWrapper id="browser-image-wrapper-v2">
                <StyledCenterImage
                  ref={browserImageRef}
                  imageSrc="registry-v2/universal-registry/website-browser-v4.jpg"
                  isScrolledIntoView
                />
              </BrowserImageWrapper>
            </StyledCenterImageShape>
          </StyledMainFlex>
        </StyledGoldThreadBack>
      </UniversalRegistryGraphicWrapper>
      <StyledMobileImage src="registry-v2/universal-registry/mobile.jpg" width="100%" />
    </StyledUniversalRegistry>
  );
};
